const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

const SWEDEN_CENTER = { lat: 62.5, lng: 16.5 };
const DEFAULT_ZOOM = 5;
const MARKER_COLOR_ACTIVE = '#E40520';
const MARKER_COLOR_MUTED = '#9e9e9e';
const MARKER_TWEEN_DURATION = 200;
const MARKER_COLOR_ENDPOINTS = new Set([
  MARKER_COLOR_ACTIVE.toLowerCase(),
  MARKER_COLOR_MUTED.toLowerCase(),
]);

const markerIconCache = {};

function parseHexColor(hex) {
  const value = hex.replace('#', '');

  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function interpolateColor(fromColor, toColor, progress) {
  const from = parseHexColor(fromColor);
  const to = parseHexColor(toColor);
  const channel = (start, end) => Math.round(start + ((end - start) * progress));

  const r = channel(from.r, to.r).toString(16).padStart(2, '0');
  const g = channel(from.g, to.g).toString(16).padStart(2, '0');
  const b = channel(from.b, to.b).toString(16).padStart(2, '0');

  return `#${r}${g}${b}`;
}

function easeInOut(progress) {
  return progress < 0.5
    ? 2 * progress * progress
    : 1 - (((-2 * progress) + 2) ** 2) / 2;
}

function debounce(fn, delay) {
  let timer;

  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

function parseConfig(root) {
  const raw = root.getAttribute('data-depot-selector-config');

  if (!raw) {
    return { markers: [] };
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return { markers: [] };
  }
}

function createMarkerIcon(color) {
  const normalizedColor = color.toLowerCase();

  if (markerIconCache[normalizedColor]) {
    return markerIconCache[normalizedColor];
  }

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
      <path fill="${normalizedColor}" d="M16 0C7.163 0 0 7.163 0 16c0 11.046 16 26 16 26s16-14.954 16-26C32 7.163 24.837 0 16 0z"/>
      <circle fill="#fff" cx="16" cy="16" r="6"/>
    </svg>
  `;

  const icon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(32, 42),
    anchor: new google.maps.Point(16, 42),
  };

  if (MARKER_COLOR_ENDPOINTS.has(normalizedColor)) {
    markerIconCache[normalizedColor] = icon;
  }

  return icon;
}

function syncMapHeight(root) {
  const top = root.getBoundingClientRect().top;
  root.style.minHeight = `${window.innerHeight - top}px`;
}

function isElementInScrollContainer(element, container) {
  if (!element || !container) {
    return true;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return (
    elementRect.top >= containerRect.top
    && elementRect.bottom <= containerRect.bottom
  );
}

function initDepotSelector(root) {
  const config = parseConfig(root);
  const canvas = root.querySelector('.depot-map-canvas');
  const list = root.querySelector('.depot-list');
  const searchInput = root.querySelector('.search-input');
  const regionNodes = [...root.querySelectorAll('.depot.region')];
  const depotNodes = [...root.querySelectorAll('.depots .depot')];
  const markerByDepotId = new Map();
  const markerColorByDepotId = new Map();
  const markerTweenByDepotId = new Map();
  let map = null;
  let activeDepotId = null;
  let hoveredDepotId = null;
  let visibleDepotIds = new Set(config.markers.map((marker) => String(marker.id)));
  let currentFilterQuery = '';

  const iconActive = () => createMarkerIcon(MARKER_COLOR_ACTIVE);

  const cancelMarkerTween = (depotId) => {
    const tween = markerTweenByDepotId.get(depotId);

    if (!tween) {
      return;
    }

    cancelAnimationFrame(tween);
    markerTweenByDepotId.delete(depotId);
  };

  const setMarkerIcon = (depotId, targetColor, { immediate = false } = {}) => {
    const marker = markerByDepotId.get(depotId);
    const normalizedTarget = targetColor.toLowerCase();

    if (!marker) {
      return;
    }

    cancelMarkerTween(depotId);

    const fromColor = markerColorByDepotId.get(depotId) || MARKER_COLOR_ACTIVE;

    if (immediate || fromColor.toLowerCase() === normalizedTarget) {
      marker.setIcon(createMarkerIcon(normalizedTarget));
      markerColorByDepotId.set(depotId, normalizedTarget);
      return;
    }

    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const linearProgress = Math.min(elapsed / MARKER_TWEEN_DURATION, 1);
      const easedProgress = easeInOut(linearProgress);
      const color = interpolateColor(fromColor, normalizedTarget, easedProgress);

      marker.setIcon(createMarkerIcon(color));
      markerColorByDepotId.set(depotId, color);

      if (linearProgress < 1) {
        const frameId = requestAnimationFrame(step);
        markerTweenByDepotId.set(depotId, frameId);
        return;
      }

      marker.setIcon(createMarkerIcon(normalizedTarget));
      markerColorByDepotId.set(depotId, normalizedTarget);
      markerTweenByDepotId.delete(depotId);
    };

    const frameId = requestAnimationFrame(step);
    markerTweenByDepotId.set(depotId, frameId);
  };

  const syncMarkerVisibility = (depotIds) => {
    visibleDepotIds = new Set(depotIds);

    markerByDepotId.forEach((marker, depotId) => {
      const isVisible = visibleDepotIds.has(depotId);
      marker.setMap(isVisible ? map : null);

      if (!isVisible) {
        cancelMarkerTween(depotId);
        return;
      }

      if (hoveredDepotId) {
        setMarkerIcon(depotId, depotId === hoveredDepotId ? MARKER_COLOR_ACTIVE : MARKER_COLOR_MUTED);
      } else {
        setMarkerIcon(depotId, MARKER_COLOR_ACTIVE);
      }
    });

    if (map && depotIds.length > 0 && currentFilterQuery !== '') {
      const bounds = new google.maps.LatLngBounds();
      let hasBounds = false;

      depotIds.forEach((depotId) => {
        const marker = markerByDepotId.get(depotId);

        if (marker) {
          bounds.extend(marker.getPosition());
          hasBounds = true;
        }
      });

      if (hasBounds) {
        map.fitBounds(bounds);

        google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          if (map.getZoom() > 10) {
            map.setZoom(10);
          }
        });
      }
    }
  };

  const updateLastVisibleMarkers = () => {
    regionNodes.forEach((regionNode) => {
      const regionDepots = [...regionNode.querySelectorAll('.depots .depot')];
      const visibleDepots = regionDepots.filter((node) => !node.hidden);

      regionDepots.forEach((node) => node.classList.remove('is-last-visible'));

      if (visibleDepots.length > 0) {
        visibleDepots[visibleDepots.length - 1].classList.add('is-last-visible');
      }
    });
  };

  const initMap = () => {
    if (!canvas || typeof google === 'undefined' || !google.maps) {
      return;
    }

    map = new google.maps.Map(canvas, {
      center: SWEDEN_CENTER,
      zoom: DEFAULT_ZOOM,
      styles: MAP_STYLES,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    const bounds = new google.maps.LatLngBounds();

    config.markers.forEach((markerData) => {
      const position = { lat: markerData.lat, lng: markerData.lng };
      const depotId = String(markerData.id);
      const marker = new google.maps.Marker({
        map,
        position,
        title: markerData.title,
        icon: iconActive(),
      });

      bounds.extend(position);
      markerByDepotId.set(depotId, marker);
      markerColorByDepotId.set(depotId, MARKER_COLOR_ACTIVE);

      marker.addListener('click', () => {
        focusDepot(depotId);
      });
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);

      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        if (map.getZoom() > 8) {
          map.setZoom(8);
        }
      });
    }

    google.maps.event.addListenerOnce(map, 'idle', () => {
      syncMapHeight(root);
    });
  };

  const clearActiveState = () => {
    depotNodes.forEach((node) => {
      node.classList.remove('is-active', 'is-flash');
    });
    activeDepotId = null;
  };

  const clearHoverState = () => {
    depotNodes.forEach((node) => node.classList.remove('is-hovered'));
    hoveredDepotId = null;
    syncMarkerVisibility([...visibleDepotIds]);
  };

  const setHoveredDepot = (depotId) => {
    hoveredDepotId = depotId;

    depotNodes.forEach((node) => {
      node.classList.toggle('is-hovered', node.getAttribute('data-depot-id') === depotId);
    });

    markerByDepotId.forEach((marker, id) => {
      if (!visibleDepotIds.has(id)) {
        return;
      }

      setMarkerIcon(id, id === depotId ? MARKER_COLOR_ACTIVE : MARKER_COLOR_MUTED);
    });
  };

  const focusDepot = (depotId) => {
    const depotNode = root.querySelector(`.depots .depot[data-depot-id="${depotId}"]`);
    const marker = markerByDepotId.get(depotId);

    if (!depotNode || depotNode.hidden) {
      return;
    }

    clearActiveState();
    depotNode.classList.add('is-active', 'is-flash');
    activeDepotId = depotId;

    window.setTimeout(() => {
      depotNode.classList.remove('is-flash');
    }, 150);

    if (list && !isElementInScrollContainer(depotNode, list)) {
      depotNode.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    }

    if (map && marker) {
      map.panTo(marker.getPosition());
      map.setZoom(Math.max(map.getZoom(), 10));
    }
  };

  const filterDepots = (query) => {
    const normalizedQuery = query.trim().toLowerCase();
    currentFilterQuery = normalizedQuery;
    let totalMatches = 0;

    regionNodes.forEach((regionNode) => {
      const regionDepots = [...regionNode.querySelectorAll('.depots .depot')];

      regionDepots.forEach((depotNode) => {
        const haystack = depotNode.getAttribute('data-search') || '';
        const regionHaystack = regionNode.getAttribute('data-search') || '';
        const matches = normalizedQuery === ''
          || haystack.includes(normalizedQuery)
          || regionHaystack.includes(normalizedQuery);

        if (matches) {
          totalMatches += 1;
        }
      });
    });

    const showAll = normalizedQuery !== '' && totalMatches === 0;
    const visibleIds = [];

    regionNodes.forEach((regionNode) => {
      let visibleDepotCount = 0;
      const regionDepots = [...regionNode.querySelectorAll('.depots .depot')];

      regionDepots.forEach((depotNode) => {
        const haystack = depotNode.getAttribute('data-search') || '';
        const regionHaystack = regionNode.getAttribute('data-search') || '';
        const matches = showAll
          || normalizedQuery === ''
          || haystack.includes(normalizedQuery)
          || regionHaystack.includes(normalizedQuery);

        depotNode.hidden = !matches;

        if (matches) {
          visibleDepotCount += 1;
          visibleIds.push(depotNode.getAttribute('data-depot-id'));
        }
      });

      regionNode.hidden = visibleDepotCount === 0;
    });

    syncMarkerVisibility(visibleIds);
    updateLastVisibleMarkers();
  };

  depotNodes.forEach((depotNode) => {
    const depotId = depotNode.getAttribute('data-depot-id');
    const pinButton = depotNode.querySelector('.map-pin');

    depotNode.addEventListener('mouseenter', () => {
      setHoveredDepot(depotId);
    });

    depotNode.addEventListener('mouseleave', () => {
      clearHoverState();
    });

    if (pinButton) {
      pinButton.addEventListener('click', () => {
        focusDepot(depotId);
      });
    }
  });

  if (searchInput) {
    searchInput.addEventListener('input', debounce((event) => {
      filterDepots(event.target.value);
    }, 200));
  }

  const handleResize = debounce(() => {
    syncMapHeight(root);

    if (map) {
      google.maps.event.trigger(map, 'resize');
    }
  }, 150);

  window.addEventListener('resize', handleResize);

  if (typeof google !== 'undefined' && google.maps) {
    initMap();
    syncMapHeight(root);
  } else {
    window.addEventListener('load', () => {
      initMap();
      syncMapHeight(root);
    });
  }

  updateLastVisibleMarkers();
}

export default function initDepotSelectors() {
  document.querySelectorAll('[data-depot-selector]').forEach((root) => {
    initDepotSelector(root);
  });
}
